import _ from "lodash";
import type { TranslatedString, TranslationKey } from "@i18n";
import { jiraController } from "../../api/jira.api";

type Translate = (key: TranslationKey, options?: Record<string, unknown>) => TranslatedString;

export type SlaReportScope = { createdBy: string; where?: unknown };

const REPORT_PAGE_SIZE = 100000;

export function readSlaReportScope(search: string): SlaReportScope {
  const params = new URLSearchParams(search);
  const createdBy = params.get("supportCreatedBy") || "createdByAnyPortalUsers";
  const whereParam = params.get("supportWhere");
  if (!whereParam) return { createdBy };
  try {
    return { createdBy, where: JSON.parse(whereParam) };
  } catch {
    return { createdBy };
  }
}

export async function fetchSlaReportTickets({ createdBy, where }: SlaReportScope): Promise<any[]> {
  const res: any = await jiraController({
    value: {
      action: "getList",
      createdBy,
      requiresCounts: false,
      take: REPORT_PAGE_SIZE,
      ...(Array.isArray(where) && where.length ? { where } : {}),
    },
  });
  return res?.data?.result ?? [];
}

export type SlaModelMix = {
  mixed: boolean;
  severityPortals: string[];
  standardPortals: string[];
};

const portalNamesWhere = (tickets: any[], severityOnly: boolean): string[] =>
  Array.from(
    new Set(
      tickets
        .filter((tk) => Boolean(_.get(tk, "portal.customSupportSla")) === severityOnly)
        .map((tk) => String(_.get(tk, "portal.name") ?? ""))
        .filter(Boolean),
    ),
  );

export function describeSlaModelMix(tickets: any[]): SlaModelMix {
  const severityPortals = portalNamesWhere(tickets, true);
  const standardPortals = portalNamesWhere(tickets, false);
  const hasSeverityOnly = tickets.some((tk) => _.get(tk, "portal.customSupportSla"));
  const hasStandard = tickets.some((tk) => !_.get(tk, "portal.customSupportSla"));
  return { mixed: hasSeverityOnly && hasStandard, severityPortals, standardPortals };
}

const portalList = (names: string[], fallback: string): string => (names.length ? names.join(", ") : fallback);

export function slaModelMixMessage(mix: SlaModelMix, t: Translate): TranslatedString {
  return t(
    "The selected tickets span a severity-only SLA portal ({{severityPortals}}) and standard impact/priority SLA portals ({{standardPortals}}). These use different SLA models and cannot be combined into one report. Filter the ticket list to a single portal and export again.",
    {
      severityPortals: portalList(mix.severityPortals, t("unnamed portal")),
      standardPortals: portalList(mix.standardPortals, t("unnamed portal")),
    },
  );
}
